import {
    NotFound,
    Submitted,
    ApplicationForm,
    Closed,
} from "@roster-technologies/rosterui";
import { message } from "antd";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    USER_PROPERTY_TYPE_TEXTAREA,
    USER_PROPERTY_TYPE_TEXTBOX,
} from "../constants";
import { fetchForm, submitForm } from "./api";
import { fetchCountries } from "./api";
import { groupParamsByKey, toBase64 } from "./helpers";
import postMessageService from "../utils/postMessage";

const WEGLOT_SRC = "https://cdn.weglot.com/weglot.min.js";

let weglotLoader = null;

const loadWeglot = () => {
    if (window.Weglot) return Promise.resolve();

    if (!weglotLoader) {
        weglotLoader = new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = WEGLOT_SRC;
            script.async = true;
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
        });
    }

    return weglotLoader;
};

const Translate = ({ apiKey, children }) => {
    useEffect(() => {
        if (!apiKey) return;

        let cancelled = false;

        loadWeglot()
            .then(() => {
                if (!cancelled) {
                    window.Weglot?.initialize({ api_key: apiKey });
                }
            })
            .catch(() => {
                console.error("Failed to load the translation script");
            });

        return () => {
            cancelled = true;
        };
    }, [apiKey]);

    return children;
};

const readBootstrappedForm = (formKey) => {
    const bootstrap = window.__APPLICATION_FORM__;

    return bootstrap?.key === formKey ? bootstrap.form : null;
};

const ApplicationFormWrapper = ({ isEmbedded }) => {
    const { formKey } = useParams();
    const bootstrappedForm = React.useRef(readBootstrappedForm(formKey)).current;
    const [formSchema, setFormSchema] = useState(
        bootstrappedForm?.success ? bootstrappedForm.result : undefined,
    );
    const [loading, setLoading] = useState(!bootstrappedForm);
    const [notFound, setNotFound] = useState(false);
    const [countries, setCountries] = useState([]);
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const formLoadedAt = React.useRef(null);

    useEffect(() => {
        fetchData();

        if (bootstrappedForm) {
            revalidateBootstrappedForm();
        }
    }, []);

    useEffect(() => {
        const { businessName, name } = formSchema ?? {};

        if (businessName && name) {
            document.title = `${businessName} - ${name}`;
        }
    }, [formSchema]);

    // Emit postMessage events when form state changes
    useEffect(() => {
        if (!formSchema || !isEmbedded) return;

        const { settings = {} } = formSchema;
        const { isDisabled } = settings;

        // Emit form viewed or closed viewed event
        postMessageService.emitFormViewed(formKey, formSchema, isDisabled);
    }, [formSchema, isEmbedded]);

    const fetchData = async () => {
        const response = bootstrappedForm ?? (await fetchForm(formKey));
        setLoading(false);

        if (response?.success) {
            formLoadedAt.current = Date.now();
            setFormSchema(response.result);

            const countriesResponse = await fetchCountries(
                response.result.userId,
            );

            if (countriesResponse?.success) {
                setCountries(countriesResponse.result);
            }
        } else {
            setNotFound(true);
        }
    };

    // The bootstrap payload rides in a document the edge may hold for an hour, and
    // a brand saving their form does not purge it. Paint from it so first paint does
    // not wait on the API, then let the API have the last word. An unchanged answer
    // keeps the current schema so nothing re-renders.
    const revalidateBootstrappedForm = async () => {
        const response = await fetchForm(formKey);

        // The request failed, so the bootstrap is still the best we have.
        if (!response) return;

        if (response.success) {
            formLoadedAt.current ??= Date.now();
            setNotFound(false);
            setFormSchema((current) =>
                JSON.stringify(current) === JSON.stringify(response.result)
                    ? current
                    : response.result,
            );
        } else {
            setNotFound(true);
        }
    };

    const onFinish = async (values) => {
        setSubmitting(true);

        const propertyKeys = Object.keys(values.properties ?? {});
        const properties = [];

        for (let i = 0; i < propertyKeys.length; i++) {
            const key = propertyKeys[i];
            let value = values.properties[key];

            if (Array.isArray(value) && !!value.length && value[0]?.originFileObj) {
                const file = value[0];

                value = {
                    base64: await toBase64(file.originFileObj),
                    contentType: file.type,
                    fileName: file.name,
                };
            }

            properties.push({ userCampaignPropertyId: `${key}`, value });
        }
        const embedUrlVar = "rf-roster-url";
        const params = new URLSearchParams(window.location.search);
        const embedUrl = params.get(embedUrlVar);
        const parentUrl = embedUrl ? new URL(embedUrl) : null;

        const urlParams = groupParamsByKey(
            isEmbedded ? parentUrl?.searchParams || params : params,
        );

        const [url] = (
            isEmbedded
                ? parentUrl?.href ||
                  window.document.referrer ||
                  window.location.href
                : window.location.href
        ).split("?");

        if (urlParams[embedUrlVar]) {
            delete urlParams[embedUrlVar];
        }

        const submitResponse = await submitForm(formKey, {
            account: values.account,
            properties: properties?.filter((property) => {
                const propObj = formSchema.properties?.find(
                    (p) => +property.userCampaignPropertyId === p.id,
                );

                if (
                    propObj?.property?.typeId === USER_PROPERTY_TYPE_TEXTBOX ||
                    propObj?.property?.typeId === USER_PROPERTY_TYPE_TEXTAREA
                ) {
                    return !!property.value?.trim();
                }

                return !!property.value;
            }),
            formUrl: { url, urlParams },
            honeypot: values.rf_comment ?? "",
            formLoadedAt: formLoadedAt.current,
            captchaToken: values.captchaToken ?? "",
        });

        setSubmitting(false);

        if (submitResponse.success) {
            // Emit success event for embedded forms
            if (isEmbedded) {
                postMessageService.emitSubmitSuccess(formKey, formSchema);
            }

            // Redirect if configured, otherwise show confirmation page
            if (submitResponse.redirectUrl) {
                if (isEmbedded) {
                    try {
                        window.top.location.href = submitResponse.redirectUrl;
                    } catch (e) {
                        // Top navigation blocked (sandboxed iframe without allow-top-navigation)
                        window.location.href = submitResponse.redirectUrl;
                    }
                } else {
                    window.location.href = submitResponse.redirectUrl;
                }
                return;
            }

            setSubmitted(true);
        } else {
            message.error({
                content:
                    "Failed to submit the form. Try again or contact support for assistance.",
                duration: 8,
            });
        }
    };

    const apiKey = formSchema?.translationApiKey;

    if (notFound) {
        return (
            <Translate apiKey={apiKey}>
                <NotFound />
            </Translate>
        );
    }

    const { settings = {} } = formSchema ?? {};
    const {
        brandLogo,
        brandLogoSize,
        isDisabled,
        confirmationMessage,
        closedMessage,
    } = settings ?? {};

    if (submitted) {
        return (
            <Translate apiKey={apiKey}>
                <Submitted
                    logo={brandLogo}
                    logoSize={brandLogoSize}
                    {...confirmationMessage}
                    isEmbedded={isEmbedded}
                />
            </Translate>
        );
    }

    if (isDisabled) {
        return (
            <Translate apiKey={apiKey}>
                <Closed
                    logo={brandLogo}
                    logoSize={brandLogoSize}
                    {...closedMessage}
                    isEmbedded={isEmbedded}
                />
            </Translate>
        );
    }

    return (
        <Translate apiKey={apiKey}>
            <ApplicationForm
                formSchema={formSchema}
                loading={loading}
                submitting={submitting}
                countries={countries?.map((el) => {
                    return { label: el.name, value: el.id, code: el.alpha2 };
                })}
                onFinish={onFinish}
                isEmbedded={isEmbedded}
                turnstileSiteKey={
                    loading
                        ? undefined
                        : process.env.REACT_APP_TURNSTILE_SITE_KEY
                }
            />
        </Translate>
    );
};

export default ApplicationFormWrapper;
